import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ConsequenceVisualKind, SlideDeckConfig, SlideVisualKind } from "../scenario/types";
import { audio } from "../lib/audio";
import { newShuffleSeed, shuffleOptions } from "../lib/shuffle";
import { activeTtsLang } from "../lib/locale";
import { ConsequenceVisual } from "../ui/ConsequenceVisual";
import { ChapterArt } from "./ChapterArt";

const CONSEQUENCE_VISUALS = new Set<SlideVisualKind>([
  "tailgate_breach", "badusb", "hackerterminal", "ransom_takeover",
  "ransomware_spread", "leaksite", "ai_shared_leak", "desk_privacy",
  "domain_spoof", "deepfake_call", "remote_takeover", "fake_meeting",
]);

/**
 * Narrated, spec-authored teaching deck staged on the office conference TV.
 * Optional hub/next-chapter controls and a closing decision are inert unless
 * the spec sets them, so existing decks keep a single continue button.
 */
export function SlideDeck({
  config,
  onComplete,
  onExit,
}: {
  config: SlideDeckConfig;
  onComplete: () => void;
  onExit?: (key: string) => void;
}) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"slides" | "decision" | "feedback" | "takeaway">("slides");
  const [picked, setPicked] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [caption, setCaption] = useState<string | null>(null);
  const mounted = useRef(true);
  const copyRef = useRef<HTMLElement>(null);
  const decisionRef = useRef<HTMLElement>(null);
  const slide = config.slides[idx];
  const last = idx === config.slides.length - 1;
  const voice = config.voiceId ?? "coral";
  const lang = config.language ?? activeTtsLang();
  const instructions =
    "Speak as a calm security-awareness instructor. Use plain language, a natural pace, and finish every sentence.";
  const decision = config.decision;
  // The know-how check shows its answers in a shuffled order (display only), fixed for this deck visit.
  const [shuffleSeed] = useState(newShuffleSeed);
  const decisionOptions = useMemo(
    () => (decision ? shuffleOptions(decision.options, shuffleSeed) : []),
    [decision, shuffleSeed],
  );
  const chosen = decision?.options.find((option) => option.id === picked);
  const correct = decision?.options.find((option) => option.correct);

  useEffect(() => audio.subscribeCaption(setCaption), []);

  useEffect(() => {
    mounted.current = true;
    config.slides.forEach((s) =>
      void audio.prefetchSpeech(s.narration, { voice, lang, instructions }),
    );
    if (decision) void audio.prefetchSpeech(decision.narration, { voice, lang, instructions });
    if (config.takeaway) void audio.prefetchSpeech(config.takeaway.narration, { voice, lang, instructions });
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setImgOk(true);
  }, [idx, slide?.imageUrl]);

  useEffect(() => {
    const line =
      phase === "decision" ? decision?.narration
        : phase === "takeaway" ? config.takeaway?.narration
          : phase === "slides" ? slide?.narration
            : null;
    if (!line) return;
    let alive = true;
    void (async () => {
      setSpeaking(true);
      await audio.speak(line, { voice, lang, instructions });
      if (alive && mounted.current) setSpeaking(false);
    })();
    return () => {
      alive = false;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, idx]);

  useLayoutEffect(() => {
    const el = phase === "decision" || phase === "feedback" ? decisionRef.current : copyRef.current;
    if (!el) return;
    const fit = () => {
      let scale = 1;
      el.style.setProperty("--fit", "1");
      for (let step = 0; step < 10; step += 1) {
        if (el.scrollHeight <= el.clientHeight + 2) break;
        scale = Math.max(0.72, Number((scale - 0.04).toFixed(2)));
        el.style.setProperty("--fit", String(scale));
        if (scale <= 0.72) break;
      }
    };
    fit();
  }, [phase, idx, picked, config, caption]);

  if (!slide) return null;

  const chapters = config.chapters;
  const chapterIndex = config.chapterIndex ?? 0;
  const hasChapterNav = !!chapters && chapters.length > 1;

  const leave = (key: string) => {
    audio.cancelSpeech();
    onExit?.(key);
  };

  const primary = () => {
    audio.cancelSpeech();
    if (!last) {
      setIdx((value) => value + 1);
      return;
    }
    if (decision && phase === "slides") {
      setPhase("decision");
      return;
    }
    onComplete();
  };

  const primaryLabel = !last
    ? "Next slide \u203A"
    : decision
      ? config.knowHowLabel ?? "Test your know-how"
      : config.continueLabel ?? "Return to the briefing";

  const goPrev = () => {
    audio.cancelSpeech();
    if (phase === "decision") {
      setPhase("slides");
      return;
    }
    if (idx > 0) setIdx((value) => value - 1);
  };

  return (
    <div className="sdeck">
      <div className="sdeck-tv">
        {hasChapterNav && chapters && (
          <nav className="sdeck-chapnav" aria-label="Chapters">
            <button
              type="button"
              className="sdeck-chapnav-arrow"
              disabled={chapterIndex <= 0}
              onClick={() => leave("prev")}
            >
              {"\u2039"}
            </button>
            <select
              className="sdeck-chapnav-select"
              aria-label="Jump to a chapter"
              value={chapters[chapterIndex]?.id ?? ""}
              onChange={(event) => {
                const next = event.target.value;
                if (next && next !== chapters[chapterIndex]?.id) leave(next);
              }}
            >
              {chapters.map((chapter, index) => (
                <option key={chapter.id} value={chapter.id}>
                  {index + 1}. {chapter.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sdeck-chapnav-arrow"
              disabled={chapterIndex >= chapters.length - 1}
              onClick={() => leave("next")}
            >
              {"\u203A"}
            </button>
            {config.hubLabel && (
              <button type="button" className="btn sdeck-hub" onClick={() => leave("hub")}>
                {config.hubLabel}
              </button>
            )}
          </nav>
        )}
        <header className="sdeck-head">
          <span className="sdeck-brand">SECURITY AWARENESS</span>
          <span className="sdeck-deck-label">{config.deckLabel ?? config.title}</span>
          <span className={`sdeck-voice ${speaking ? "is-speaking" : ""}`} aria-label={speaking ? "Narrating" : "Narration complete"}>
            <i /><i /><i />
          </span>
        </header>

        {phase === "takeaway" && config.takeaway ? (
          <main className="sdeck-slide sdeck-slide-full">
            <section ref={copyRef} className="sdeck-copy sdeck-copy-center">
              {config.takeaway.kicker && <div className="sdeck-kicker">{config.takeaway.kicker}</div>}
              <h1>{config.takeaway.title}</h1>
              <SlideProse body={config.takeaway.body} bullets={config.takeaway.bullets} />
            </section>
          </main>
        ) : phase === "slides" ? (
          <main className={`sdeck-slide${slide.art ? " sdeck-slide-taught" : slide.imageUrl || (slide.visual && !config.chapters) ? "" : " sdeck-slide-full"}`} key={idx}>
            <section ref={copyRef} className="sdeck-copy">
              {slide.kicker && <div className="sdeck-kicker">{slide.kicker}</div>}
              <h1>{slide.title}</h1>
              <SlideProse body={slide.body} bullets={slide.bullets} />
            </section>
            {slide.art ? (
              <section className="sdeck-art" aria-hidden>
                <ChapterArt id={slide.art} />
              </section>
            ) : slide.imageUrl && imgOk ? (
              <section className="sdeck-art" aria-hidden>
                <img className="sdeck-photo" src={slide.imageUrl} alt="" onError={() => setImgOk(false)} />
              </section>
            ) : slide.visual && !config.chapters ? (
              <section className="sdeck-art" aria-hidden>
                <SlideVisual kind={slide.visual} stage={idx + 1} />
              </section>
            ) : null}
          </main>
        ) : (
          <main ref={decisionRef} className="sdeck-slide sdeck-decision">
            <DecisionPrompt prompt={decision?.prompt ?? ""} knowHow={config.knowHowLabel} />
            <div className="sdeck-options">
              {decisionOptions.map((option, index) => {
                const state =
                  phase === "feedback" && option.id === picked
                    ? option.correct ? "is-correct" : "is-wrong"
                    : phase === "feedback" && option.correct
                      ? "is-answer"
                      : "";
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`sdeck-opt ${state}`}
                    disabled={phase === "feedback"}
                    onClick={() => {
                      if (phase !== "decision") return;
                      audio.cancelSpeech();
                      setPicked(option.id);
                      setPhase("feedback");
                    }}
                  >
                    <span className="sdeck-opt-mark" aria-hidden>{String.fromCharCode(65 + index)}</span>
                    <span className="sdeck-opt-text">{option.label}</span>
                  </button>
                );
              })}
            </div>
            {phase === "feedback" && chosen && (
              <p className="sdeck-feedback">
                {chosen.feedback}
                {!chosen.correct && correct ? ` Better action: ${correct.label}` : ""}
              </p>
            )}
          </main>
        )}

        <NarrationStrip text={caption} />

        <footer className="sdeck-foot">
          {phase === "slides" ? (
            <div className="sdeck-progress" aria-label={`Slide ${idx + 1} of ${config.slides.length}`}>
              {config.slides.map((_, dot) => (
                <span key={dot} className={dot < idx ? "done" : dot === idx ? "on" : ""} />
              ))}
            </div>
          ) : (
            <span className="sdeck-count sdeck-count-fill">{phase === "takeaway" ? "" : "Decision"}</span>
          )}
          {phase === "slides" && <span className="sdeck-count">{idx + 1} / {config.slides.length}</span>}
          <span className="sdeck-nav">
            {config.hubLabel && !hasChapterNav && (
              <button type="button" className="btn sdeck-hub" onClick={() => leave("hub")}>
                {config.hubLabel}
              </button>
            )}
            {config.nextChapterLabel && !hasChapterNav && (
              <button type="button" className="btn sdeck-chapter" onClick={() => leave("next")}>
                {config.nextChapterLabel}
              </button>
            )}
            {(phase === "slides" || phase === "decision") && (
              <button type="button" className="btn sdeck-prev" onClick={goPrev} disabled={phase === "slides" && idx === 0}>
                {"\u2039 Previous slide"}
              </button>
            )}
            {phase === "slides" && (
              <button type="button" className="btn sdeck-next" onClick={primary}>
                {primaryLabel}
              </button>
            )}
            {phase === "feedback" && config.takeaway && (
              <button type="button" className="btn sdeck-next" onClick={() => { audio.cancelSpeech(); setPhase("takeaway"); }}>
                Key takeaway
              </button>
            )}
            {phase === "takeaway" && (
              <button
                type="button"
                className="btn sdeck-next"
                onClick={() => {
                  audio.cancelSpeech();
                  const lastChapter = !hasChapterNav || chapterIndex >= (chapters?.length ?? 1) - 1;
                  if (!lastChapter) leave("next");
                  else if (config.hubLabel) leave("hub");
                  else onComplete();
                }}
              >
                {!hasChapterNav || chapterIndex >= (chapters?.length ?? 1) - 1
                  ? "Back to overview"
                  : "Next chapter \u203A"}
              </button>
            )}
            {phase === "feedback" && !config.takeaway && !hasChapterNav && !config.nextChapterLabel && (
              <button type="button" className="btn sdeck-next" onClick={() => { audio.cancelSpeech(); onComplete(); }}>
                {config.continueLabel ?? "Return to the briefing"}
              </button>
            )}
          </span>
        </footer>
      </div>
    </div>
  );
}

const MARK = [
  "higher-risk personal information",
  "confidential business information",
  "personal information",
  "unlabelled document",
  "ordinary filename",
  "warning label",
  "not automatically public",
];

function markText(text: string) {
  const pattern = new RegExp(`(${MARK.map((phrase) => phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const parts = text.split(pattern);
  return parts.map((part, index) =>
    MARK.some((phrase) => phrase.toLowerCase() === part.toLowerCase())
      ? <strong key={index} className="sdeck-em">{part}</strong>
      : <span key={index}>{part}</span>,
  );
}

function splitSentences(text: string) {
  return text.split(/(?<=[.!?])\s+(?=[A-Z“"'])/).map((part) => part.trim()).filter(Boolean);
}

function formatClock(seconds: number) {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** Spoken line, shown inside the television while that line is playing. */
function NarrationStrip({ text }: { text: string | null }) {
  const [progress, setProgress] = useState({ current: 0, duration: 0, playing: false });
  const [full, setFull] = useState(false);
  const linesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setFull(false);
    if (!text) return;
    const tick = () => {
      const next = audio.speechProgress();
      if (next) setProgress(next);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [text]);
  const sentences = text ? splitSentences(text) : [];
  const ratio = progress.duration > 0 ? Math.min(0.999, progress.current / progress.duration) : 0;
  const active = sentences.length ? Math.min(sentences.length - 1, Math.floor(ratio * sentences.length)) : 0;
  useEffect(() => {
    const box = linesRef.current;
    const current = box?.querySelector(".is-now");
    if (!box || !current || full) return;
    const top = current.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTop = top;
  }, [active, full, text]);
  if (!text) return null;
  return (
    <div className="sdeck-narr" role="status" aria-live="polite">
      <div className="sdeck-narr-head">
        <span className="sdeck-narr-ico" aria-hidden>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M4 9v6h4l5 4V5L8 9H4zm11.5 3a3.5 3.5 0 0 0-1.8-3.06v6.12A3.5 3.5 0 0 0 15.5 12z"/></svg>
        </span>
        <strong>Narration</strong>
        {progress.playing && (
          <span className="sdeck-narr-live">
            <i /> Playing
            <span className="sdeck-narr-bars" aria-hidden><b /><b /><b /></span>
          </span>
        )}
      </div>
      <div className={`sdeck-narr-lines${full ? " is-full" : ""}`} ref={linesRef}>
        <p className="sdeck-narr-flow">
          {sentences.map((sentence, index) => (
            <span key={index} className={index === active ? "is-now" : ""}>{sentence} </span>
          ))}
        </p>
      </div>
      <div className="sdeck-narr-time">
        <span>{formatClock(progress.current)} / {formatClock(progress.duration)}</span>
        <span className="sdeck-narr-track" aria-hidden>
          <i style={{ width: `${Math.round(ratio * 100)}%` }} />
        </span>
        {sentences.length > 1 && (
          <button type="button" className="sdeck-narr-more" onClick={() => setFull((value) => !value)}>
            {full ? "Hide transcript" : "Show full transcript"}
          </button>
        )}
      </div>
    </div>
  );
}

function isPoint(line: string) {
  const match = /^([^:]{2,56}):\s+\S/.exec(line);
  return !!match && !/^https?$/i.test(match[1]);
}

function isDashPoint(line: string) {
  return /^.{2,42}\s[—–]\s\S/.test(line);
}

function isLead(line: string) {
  return /:\s*$/.test(line) && !isPoint(line);
}

function isExample(line: string) {
  return /^(A|An)\s+\S/.test(line) && line.length < 150 && !isDashPoint(line) && !isPoint(line);
}

function isCallout(line: string) {
  return /not automatically|warning signs were|no label does not/i.test(line);
}

function PointText({ item }: { item: string }) {
  const colon = /^([^:]{2,56}):\s+([\s\S]+)$/.exec(item);
  const dash = /^(.{2,42})\s[—–]\s+([\s\S]+)$/.exec(item);
  const match = colon && !/^https?$/i.test(colon[1]) ? colon : dash;
  if (!match) return <>{markText(item)}</>;
  return (
    <>
      <strong className="sdeck-term">{match[1]}</strong>
      <span>{match[2]}</span>
    </>
  );
}

function expandLines(lines: string[]) {
  const out: string[] = [];
  for (const line of lines) {
    if (isDashPoint(line) || isPoint(line) || isLead(line)) {
      out.push(line);
      continue;
    }
    const parts = line.split(/(?<=[.!?])\s+(?=[A-Z“"'])/).map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 3) out.push(...parts);
    else out.push(line);
  }
  return out;
}

type ProseBlock =
  | { kind: "p" | "lead" | "callout"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "steps"; items: string[] };

function groupLines(lines: string[]): ProseBlock[] {
  const blocks: ProseBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isCallout(line)) {
      blocks.push({ kind: "callout", text: line });
      i += 1;
      continue;
    }
    if (isLead(line)) {
      blocks.push({ kind: "lead", text: line.replace(/:\s*$/, "") });
      i += 1;
      const items: string[] = [];
      if (i < lines.length && isExample(lines[i])) {
        while (i < lines.length && isExample(lines[i])) {
          items.push(lines[i]);
          i += 1;
        }
      } else {
        while (i < lines.length && !isLead(lines[i]) && !isDashPoint(lines[i]) && !isPoint(lines[i]) && lines[i].length < 170 && !isCallout(lines[i])) {
          items.push(lines[i]);
          i += 1;
        }
      }
      if (items.length) blocks.push({ kind: "list", items });
      continue;
    }
    if (isDashPoint(line) || isPoint(line)) {
      const items: string[] = [];
      while (i < lines.length && (isDashPoint(lines[i]) || isPoint(lines[i]))) {
        items.push(lines[i]);
        i += 1;
      }
      blocks.push({ kind: "list", items });
      continue;
    }
    if (isExample(line)) {
      const items: string[] = [];
      while (i < lines.length && isExample(lines[i])) {
        items.push(lines[i]);
        i += 1;
      }
      if (items.length >= 2) blocks.push({ kind: "list", items });
      else blocks.push({ kind: "p", text: items[0] });
      continue;
    }
    const run: string[] = [];
    while (
      i < lines.length &&
      !isLead(lines[i]) &&
      !isDashPoint(lines[i]) &&
      !isPoint(lines[i]) &&
      !isExample(lines[i]) &&
      !isCallout(lines[i])
    ) {
      run.push(lines[i]);
      i += 1;
    }
    if (run.length >= 4) blocks.push({ kind: "steps", items: run });
    else run.forEach((text) => blocks.push({ kind: "p", text }));
  }
  return blocks;
}

/** Turns lesson copy into a lead, highlighted points, and a closing line. */
function SlideProse({ body, bullets }: { body: string; bullets?: string[] }) {
  const lines = expandLines(body.split("\n").map((line) => line.trim()).filter(Boolean));
  const blocks = groupLines(lines);
  const extra = (bullets ?? []).filter(Boolean);
  return (
    <>
      {blocks.map((block, index) => {
        if (block.kind === "lead") return <p key={index} className="sdeck-section">{block.text}</p>;
        if (block.kind === "callout") return <p key={index} className="sdeck-callout">{markText(block.text)}</p>;
        if (block.kind === "steps") {
          return (
            <ol key={index} className="sdeck-steps">
              {block.items.map((item) => <li key={item}>{markText(item)}</li>)}
            </ol>
          );
        }
        if (block.kind === "list") {
          return (
            <ul key={index} className="sdeck-points">
              {block.items.map((item) => <li key={item}><PointText item={item} /></li>)}
            </ul>
          );
        }
        return <p key={index} className="sdeck-lead">{markText(block.text)}</p>;
      })}
      {extra.length > 0 && (
        <ul className="sdeck-points">
          {extra.map((item) => <li key={item}><PointText item={item} /></li>)}
        </ul>
      )}
    </>
  );
}

function parseDecisionPrompt(prompt: string): { title?: string; body: string; question?: string } {
  let lines = prompt.split("\n").map((line) => line.trim()).filter(Boolean);
  let title: string | undefined;
  if (lines.length > 1 && /^your decision\b/i.test(lines[0]) && lines[0].length < 96) {
    title = lines[0].replace(/^your decision:\s*/i, "");
    title = title.charAt(0).toUpperCase() + title.slice(1);
    lines = lines.slice(1);
  }
  let question: string | undefined;
  const isQuestion = (value: string) => /\?\s*$/.test(value) && value.length < 220;
  if (lines.length > 1 && isQuestion(lines[lines.length - 1])) {
    question = lines[lines.length - 1];
    lines = lines.slice(0, -1);
  } else if (lines.length === 1) {
    const parts = splitSentences(lines[0]);
    const last = parts[parts.length - 1];
    if (parts.length > 1 && last && isQuestion(last)) {
      question = last;
      lines = [parts.slice(0, -1).join(" ")];
    }
  }
  return { title, body: lines.join("\n"), question };
}

/** Know-how check: situation, a visible question, then answers the learner clicks. */
function DecisionPrompt({ prompt, knowHow }: { prompt: string; knowHow?: string }) {
  const parsed = parseDecisionPrompt(prompt);
  const heading = parsed.title || parsed.question;
  const question = parsed.title ? parsed.question : undefined;
  return (
    <>
      <div className="sdeck-kicker">{knowHow ?? "Test your know-how"}</div>
      {heading && <h1>{heading}</h1>}
      {parsed.body && (
        <div className="sdeck-copy sdeck-decision-copy">
          <SlideProse body={parsed.body} />
        </div>
      )}
      {question && <p className="sdeck-question">{question}</p>}
      <p className="sdeck-clickcue">Click one answer below.</p>
    </>
  );
}

function SlideVisual({ kind, stage }: { kind: SlideVisualKind; stage: number }) {
  if (CONSEQUENCE_VISUALS.has(kind)) {
    return <ConsequenceVisual kind={kind as ConsequenceVisualKind} stage={Math.max(2, stage)} />;
  }

  if (kind === "phishing_page") {
    return (
      <div className="sdv sdv-phish-native">
        <div className="sdv-native-browser">
          <div className="sdv-native-bar">
            <span className="sdv-native-dots">{"\u25CF \u25CF \u25CF"}</span>
            <span className="sdv-native-url">{"\uD83D\uDD12"} northbay-<b>id-secure</b>.com</span>
          </div>
          <div className="sdv-native-login">
            <span className="sdv-native-logo">N</span>
            <strong>NorthBay Financial</strong>
            <i>Email address</i><i>Password</i><button type="button">Sign in</button>
          </div>
        </div>
        <div className="sdv-native-compare">
          REAL ADDRESS <b>northbayfinancial.com</b><span>THIS PAGE IS A COPY</span>
        </div>
      </div>
    );
  }

  if (kind === "dark_market") {
    return (
      <div className="sdv sdv-market-native">
        <div className="sdv-market-window">
          <div className="sdv-native-bar">
            <span className="sdv-native-dots">{"\u25CF \u25CF \u25CF"}</span>
            <span className="sdv-native-url">onion / credential-market / latest</span>
          </div>
          <div className="sdv-market-head"><b>STOLEN LOGINS FOR SALE</b><span>NEW TODAY</span></div>
          <div className="sdv-market-rows">
            {[
              ["Company email login", "WORKS"],
              ["Stay-signed-in keys", "NO CODE NEEDED"],
              ["Remote access to a company", "WORKS"],
              ["Customer list of a hacked shop", "2.4M LOGINS"],
            ].map(([name, status], index) => (
              <div key={name} style={{ animationDelay: `${index * 180}ms` }}>
                <span>{name}</span><b>{status}</b>
              </div>
            ))}
          </div>
        </div>
        <div className="sdv-market-flow"><span>STOLEN</span><i />FOR SALE<i />USED</div>
      </div>
    );
  }

  if (kind === "credential_vault") {
    return (
      <div className="sdv sdv-vault">
        <div className="sdv-browser"><span /><span /><span /></div>
        <div className="sdv-vault-card">
          <b>Saved passwords</b>
          {["Mail", "Cloud drive", "Shop", "VPN"].map((name, i) => (
            <div key={name} style={{ animationDelay: `${i * 180}ms` }}><span>{name}</span><code>••••••••</code></div>
          ))}
        </div>
        <div className="sdv-copy-arrow">COPYING <i /></div>
        <div className="sdv-attacker">SENT TO<br />CRIMINAL</div>
      </div>
    );
  }

  if (kind === "third_party_breach") {
    return (
      <div className="sdv sdv-breach">
        <div className="sdv-shop">FITNESS APP</div>
        <div className="sdv-db">
          {["name", "email", "password", "last login"].map((row, i) => <span key={row} style={{ animationDelay: `${i * 160}ms` }}>{row}</span>)}
        </div>
        <div className="sdv-crack">CUSTOMER LIST STOLEN</div>
      </div>
    );
  }

  if (kind === "human_social") {
    return (
      <div className="sdv sdv-human">
        <div className="sdv-call"><b>IT Service Desk</b><span>“I need to verify your login.”</span></div>
        <div className="sdv-chat"><b>Support chat</b><span>“Send the code you just received.”</span></div>
        <div className="sdv-human-badge">THEY SIMPLY ASK</div>
      </div>
    );
  }
  if (kind === "login_found") {
    return (
      <div className="sdv sdv-human">
        <div className="sdv-call"><b>Security notice</b><span>Your work email and password are on a list of stolen logins.</span></div>
        <div className="sdv-chat"><b>What a criminal can do</b><span>Sign in as you: email, files, chats, company systems.</span></div>
        <div className="sdv-human-badge">HOW IT GOT OUT IS UNKNOWN</div>
      </div>
    );
  }
  if (kind === "protect_steps") {
    return (
      <div className="sdv sdv-human">
        <div className="sdv-call"><b>1. New password</b><span>Here, and on every site where you used the same one.</span></div>
        <div className="sdv-chat"><b>2. Two-step sign-in</b><span>A code or app tap on top of the password.</span></div>
        <div className="sdv-call"><b>3. Tell IT</b><span>If a program might be hiding on your computer.</span></div>
        <div className="sdv-human-badge">FAST BEATS PERFECT</div>
      </div>
    );
  }

  if (kind === "credential_stuffing") {
    return (
      <div className="sdv sdv-stuffing">
        <div className="sdv-credential">alex@company.com<br /><b>One leaked password</b></div>
        <div className="sdv-bot">TRIED EVERYWHERE</div>
        <div className="sdv-sites">
          {["MAIL", "REMOTE", "SHOP", "CLOUD", "BANK", "SOCIAL"].map((site, i) => (
            <span key={site} className={i === 1 || i === 3 ? "hit" : ""} style={{ animationDelay: `${i * 150}ms` }}>{site}</span>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "session_cookie") {
    return (
      <div className="sdv sdv-cookie">
        <div className="sdv-locks"><span>PASSWORD</span><span>PHONE CODE</span></div>
        <div className="sdv-cookie-chip">SIGNED-IN<br />KEY</div>
        <div className="sdv-cookie-path">SKIPS LOGIN <i /></div>
        <div className="sdv-account">ACCOUNT OPEN</div>
      </div>
    );
  }

  if (kind === "mailbox_trust") {
    return (
      <div className="sdv sdv-mailbox">
        <div className="sdv-thread">
          <b>From: maria@company.com</b>
          <span>Re: Updated payment details</span>
          <p>Hi team — use the new account below before 3pm.</p>
        </div>
        <div className="sdv-trust-stamp">REAL MAILBOX<br />REAL THREAD</div>
      </div>
    );
  }

  return (
    <div className="sdv sdv-resale">
      <div className="sdv-access-card"><span>WAY INTO A COMPANY</span><b>WORKING LOGIN</b></div>
      <div className="sdv-market-arrow">SOLD TO <i /></div>
      <div className="sdv-buyers"><span>Fraud gang</span><span>Data thief</span><span>Ransomware group</span></div>
    </div>
  );
}
